import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import "./DraftPicks.css";
import DraftCalculator from "./DraftCalculator";

/* ---------------- constants ---------------- */
const VALID_TABS = ["Picks", "Teams", "Trades", "Calc", "Sheet"];
const TOTAL_PICKS = 204;
const TEAMS_PER_ROUND = 12;

/* ---------------- data (static) ---------------- */
const teams = [
  "All Teams",
  "AJ",
  "Chris",
  "Bobby",
  "Terry",
  "Derrick",
  "Gary",
  "Filter",
  "Manny",
  "Kenny",
  "Franco",
  "'Todd'",
  "Scheck",
];

const trades = [
  { tradeId: 1, from: "Filter", to: "Gary", picks: ["1.7"], date: "August 1, 2025" },
  { tradeId: 1, from: "Gary", to: "Filter", picks: ["1.6"], date: "August 1, 2025" },
  { tradeId: 2, from: "Filter", to: "Scheck", picks: ["3.7", "4.6"], date: "August 1, 2025" },
  { tradeId: 2, from: "Scheck", to: "Filter", picks: ["2.1", "12.1"], date: "August 1, 2025" },
  { tradeId: 3, from: "Scheck", to: "Chris", picks: ["1.12", "13.12"], date: "August 1, 2025" },
  { tradeId: 3, from: "Chris", to: "Scheck", picks: ["3.2", "4.11"], date: "August 1, 2025" },
  { tradeId: 4, from: "Filter", to: "Scheck", picks: ["2.6", "5.7"], date: "August 1, 2025" },
  { tradeId: 4, from: "Scheck", to: "Filter", picks: ["3.2", "4.6"], date: "August 1, 2025" },
  { tradeId: 5, from: "Chris", to: "Manny", picks: ["2.11", "7.2"], date: "August 1, 2025" },
  { tradeId: 5, from: "Manny", to: "Chris", picks: ["3.8", "5.8"], date: "August 1, 2025" },
  { tradeId: 6, from: "Gary", to: "Bobby", picks: ["3.6", "6.7"], date: "August 1, 2025" },
  { tradeId: 6, from: "Bobby", to: "Gary", picks: ["2.10", "9.3"], date: "August 1, 2025" },
  { tradeId: 7, from: "Filter", to: "Chris", picks: ["2.1", "4.6"], date: "August 9, 2025" },
  { tradeId: 7, from: "Chris", to: "Filter", picks: ["1.2", "12.11"], date: "August 9, 2025" },
];

/* ---------------- helpers ---------------- */
const sanitizeTab = (val) => {
  if (!val) return "Picks";
  const normalized =
    val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
  return VALID_TABS.includes(normalized) ? normalized : "Picks";
};

const buildBasePicks = () => {
  const arr = new Array(TOTAL_PICKS);
  for (let i = 0; i < TOTAL_PICKS; i++) {
    const round = Math.floor(i / TEAMS_PER_ROUND) + 1;
    const pickInRound = (i % TEAMS_PER_ROUND) + 1;
    const overall = i + 1;
    const teamIndex =
      round % 2 === 0 ? TEAMS_PER_ROUND - pickInRound : pickInRound - 1;
    const name = teams[teamIndex + 1];

    arr[i] = {
      number: `${round}.${pickInRound}`,
      overall,
      team: name,
      originalTeam: name,
      traded: false,
      from: null,
      tradeId: undefined,
    };
  }
  return arr;
};

/* ---------------- shared UI: PopoverSelect ---------------- */
function PopoverSelect({
  label = "Select",
  value,
  options,
  onChange,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const handlePick = (val) => {
    onChange(val);
    close();
  };

  return (
    <div className="psel">
      <button
        type="button"
        className={`psel-btn ${value !== options[0] ? "psel-btn--active" : ""}`}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel || label}
        title={label}
      >
        <span className="psel-text">{value}</span>
        <svg
          className={`psel-caret ${open ? "is-open" : ""}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            d="M6 9l6 6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="psel-pop" role="menu" ref={popRef}>
          <div className="psel-list">
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                role="menuitem"
                className={`psel-item ${value === opt ? "is-active" : ""}`}
                onClick={() => handlePick(opt)}
              >
                {opt}
              </button>
            ))}
            {value !== options[0] && (
              <button
                type="button"
                className="psel-clear"
                onClick={() => handlePick(options[0])}
              >
                Clear filter
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- main ---------------- */
export default function DraftPicks() {
  /* ---------- mark Draft tab active + dot (no navbar changes needed) ---------- */
  useEffect(() => {
    document.body.classList.add("is-draft-page");
    return () => document.body.classList.remove("is-draft-page");
  }, []);

  /* ---------- URL tab ---------- */
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = sanitizeTab(searchParams.get("tab"));
  const [view, setView] = useState(urlTab);

  // ensure URL always has valid ?tab= without piling history
  useEffect(() => {
    if (urlTab !== searchParams.get("tab")) {
      const sp = new URLSearchParams(searchParams);
      sp.set("tab", urlTab);
      setSearchParams(sp, { replace: true });
    }
  }, [urlTab, searchParams, setSearchParams]);

  // sync local view if nav updates URL
  useEffect(() => {
    const current = sanitizeTab(searchParams.get("tab"));
    if (current !== view) setView(current);
  }, [searchParams, view]);

  /* ---------- picks (memoized) ---------- */
  const picks = useMemo(() => {
    const base = buildBasePicks();

    // map pick number -> index for O(1) updates
    const indexByNumber = new Map();
    for (let i = 0; i < base.length; i++) {
      indexByNumber.set(base[i].number, i);
    }

    // apply trades
    for (const tr of trades) {
      for (const pickNumber of tr.picks) {
        const idx = indexByNumber.get(pickNumber);
        if (idx != null) {
          const p = base[idx];
          base[idx] = {
            ...p,
            team: tr.to,
            traded: true,
            from: tr.from,
            tradeId: tr.tradeId,
          };
        }
      }
    }

    return base;
  }, []);

/* ---------- Teams view ---------- */
const [selectedTeam, setSelectedTeam] = useState("");
const handleTeamChange = useCallback((val) => setSelectedTeam(val), []);

// Pick a random team (excluding "All Teams") on load
useEffect(() => {
  const teamList = teams.slice(1); // remove "All Teams"
  const random = teamList[Math.floor(Math.random() * teamList.length)];
  setSelectedTeam(random);
}, []);

  /* ---------- Trades controls: sort by tradeId + team filter ---------- */
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc' (desc=newest first)
  const [teamFilter, setTeamFilter] = useState("All Teams");

  const toggleSortDir = useCallback(
    () => setSortDir((s) => (s === "desc" ? "asc" : "desc")),
    []
  );

  // build trade groups (filtered by team, sorted by tradeId)
  const tradeGroups = useMemo(() => {
    const ids = Array.from(new Set(trades.map((t) => t.tradeId)));

    // filter by team if needed
    const filteredIds =
      teamFilter === "All Teams"
        ? ids
        : ids.filter((id) => {
            const rows = trades.filter((t) => t.tradeId === id);
            return rows.some((r) => r.from === teamFilter || r.to === teamFilter);
          });

    // sort by tradeId numeric
    filteredIds.sort((a, b) => (sortDir === "asc" ? a - b : b - a));

    // shape groups (use first row date for display)
    return filteredIds.map((id) => {
      const rows = trades.filter((t) => t.tradeId === id);
      const date = rows[0]?.date ?? "";
      return { id, date, rows };
    });
  }, [sortDir, teamFilter]);

  /* ---------- render ---------- */
  return (
    <div className="draft-picks-container">

      {/* Picks */}
      {view === "Picks" && (
        <div className="picks-page">
          <div className="picks-hero">
            <div className="hero-content">
              <h1 className="hero-title">Draft Board</h1>
              <p className="hero-subtitle">Complete overview of all 204 picks in the 2025 draft</p>
            </div>
          </div>
          
          <div className="picks-container">
            <table className="draft-picks-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Pick</th>
                  <th>Team</th>
                  <th>Trade</th>
                </tr>
              </thead>
              <tbody>
                {picks.map((pick) => (
                  <tr
                    key={pick.overall}
                    className={
                      Math.floor((pick.overall - 1) / TEAMS_PER_ROUND) % 2 === 0
                        ? "even-round"
                        : "odd-round"
                    }
                  >
                    <td className="overall">{pick.overall}</td>
                    <td className="number">{pick.number}</td>
                    <td className="team">{pick.team}</td>
                    <td className="trade">
                      {pick.traded && (
                        <span className="trade-badge" title={`Traded from ${pick.from}`}>
                          from {pick.from}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Teams */}
      {view === "Teams" && (
        <div className="teams-page">
          <div className="teams-hero">
            <div className="hero-content">
              <h1 className="hero-title">Team Picks</h1>
              <p className="hero-subtitle">View picks by individual team</p>
            </div>
          </div>
          
          <div className="teams-container">
            <div className="teams-controls">
              <PopoverSelect
                label="Filter by team"
                ariaLabel="Filter by team"
                value={selectedTeam}
                options={teams.slice(1)} // no "All Teams" in the dropdown
                onChange={handleTeamChange}
              />
            </div>

            <table className="team-picks-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Pick</th>
                  <th>Origin</th>
                </tr>
              </thead>
              <tbody>
                {picks
                  .filter((p) => p.team === selectedTeam) // no "All Teams" check needed
                  .map((pick, idx) => (
                    <tr
                      key={`${pick.overall}-${idx}`}
                      className={`${
                        Math.floor((pick.overall - 1) / TEAMS_PER_ROUND) % 2 === 0
                          ? "even-round"
                          : "odd-round"
                      } ${idx === 7 ? "ninth-row" : ""}`}
                    >
                      <td className="overall">{pick.overall}</td>
                      <td className="number">{pick.number}</td>
                      <td className="team">{pick.from}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Trades */}
      {view === "Trades" && (
        <div className="trades-page">
          <div className="trades-hero">
            <div className="hero-content">
              <h1 className="hero-title">Trade History</h1>
              <p className="hero-subtitle">Track all the moves that shape the league's landscape</p>
            </div>
          </div>
          
          <div className="trades-container">
              <div className="trades-controls">
              <button
                type="button"
                className="sort-button sort-button--icon"
                onClick={toggleSortDir}
                aria-label={`Sort trades ${
                  sortDir === "desc"
                    ? "descending (newest first)"
                    : "ascending (oldest first)"
                }`}
                title="Sort by trade #"
              >
                <span className="sort-icon" aria-hidden="true">
                  {sortDir === "desc" ? (
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path d="M12 20V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M7 9l5-5 5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path d="M12 4v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M17 15l-5 5-5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
              </button>

              <div className="filter-control">
                <PopoverSelect
                  label="Filter by team"
                  ariaLabel="Filter by team"
                  value={teamFilter}
                  options={teams}
                  onChange={setTeamFilter}
                />
              </div>
            </div>

            <div className="trades-grid">
              {tradeGroups.map((g) => (
                <div key={g.id} className="trade-card">
                  <div className="trade-header">
                    <div className="trade-number">
                      <span className="trade-label">Trade</span>
                      <span className="trade-id">#{g.id}</span>
                    </div>
                    <div className="trade-date">{g.date}</div>
                  </div>
                  
                  <div className="trade-partners">
                    {g.rows.map((row, i) => (
                      <div key={i} className="trade-partner">
                        <div className="partner-info">
                          <div className="partner-name">{row.from}</div>
                          <div className="trade-action">sends</div>
                          <div className="partner-picks">
                            {row.picks.map((pick, pickIndex) => (
                              <span key={pickIndex} className="pick-badge">
                                {pick}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {tradeGroups.length === 0 && (
                <div className="trades-empty">
                  <div className="empty-content">
                    <div className="empty-icon">🤝</div>
                    <h3 className="empty-title">No Trades Yet</h3>
                    <p className="empty-message">The league is still in the early stages. Trades will appear here once they happen!</p>
                    <div className="empty-gif">
                      <iframe
                        src="https://giphy.com/embed/C4etHHKiPgDqKCPP0m"
                        width="100%"
                        height="200"
                        style={{ border: "none", maxWidth: "300px" }}
                        allowFullScreen
                        title="No trades yet GIF"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Calc (unchanged component, but PopoverSelect is available to reuse there) */}
      {view === "Calc" && <DraftCalculator />}

      {/* Official Google Sheet */}
      {view === "Sheet" && (
        <div className="sheet-page">
          <div className="sheet-hero">
            <div className="hero-content">
              <h1 className="hero-title">Official Google Sheet</h1>
              <p className="hero-subtitle">Live league spreadsheet</p>
            </div>
          </div>
          <div className="sheet-container">
            <div className="sheet-actions">
              <a
                className="button btn-accent btn-pill"
                href="https://docs.google.com/spreadsheets/d/e/2PACX-1vR0f3eQJ29zv-Iz8I4OUvWG4_5KKACh7uqbyhEr3d6MkLb4Snqy_udlCuWJfbNclU80QZN6kri1vUDR/pubhtml"
                target="_blank"
                rel="noopener noreferrer"
              >
                Click to see the full sheet ›
              </a>
            </div>
            <iframe
              className="sheet-embed"
              src="https://docs.google.com/spreadsheets/d/e/2PACX-1vR0f3eQJ29zv-Iz8I4OUvWG4_5KKACh7uqbyhEr3d6MkLb4Snqy_udlCuWJfbNclU80QZN6kri1vUDR/pubhtml?widget=true&headers=false"
              title="Official Google Sheet"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      )}
    </div>
  );
}